/**
 * SHEEP MADE SIMPLE
 * Shared Seasonal Task Metadata
 * Version 1.0.0
 */
window.SMSSeasonalTaskMetadata = (() => {
  "use strict";

  const TASK_SCHEMA_VERSION = 1;
  const VALID_SEASONS = Object.freeze(["winter", "spring", "summer", "autumn"]);

  function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }

  function cloneData(data) {
    if (typeof structuredClone === "function") return structuredClone(data);
    return JSON.parse(JSON.stringify(data));
  }

  function isValidIsoDate(value) {
    return typeof value === "string" && value.trim() !== "" && !Number.isNaN(Date.parse(value));
  }

  function validateTaskInput({ season, year, taskId, data, allowedTaskIds }) {
    const errors = [];

    if (!VALID_SEASONS.includes(season)) errors.push(`Invalid season: ${season}`);
    if (!Number.isInteger(year) || year < 2020 || year > 2100) {
      errors.push(`Invalid seasonal plan year: ${year}`);
    }
    if (typeof taskId !== "string" || taskId.trim() === "") {
      errors.push("A valid taskId is required.");
    }
    if (Array.isArray(allowedTaskIds) && !allowedTaskIds.includes(taskId)) {
      errors.push(`Task "${taskId}" is not registered for ${season} ${year}.`);
    }
    if (!isPlainObject(data)) errors.push("Task data must be a plain object.");

    return { valid: errors.length === 0, errors };
  }

  function buildTaskRecord({
    season,
    year,
    taskId,
    data,
    completed = true,
    existingTask = null,
    allowedTaskIds = null,
    timestamp = null
  }) {
    const validation = validateTaskInput({ season, year, taskId, data, allowedTaskIds });
    if (!validation.valid) return { success: false, errors: validation.errors, task: null };

    const now = timestamp || new Date().toISOString();
    if (!isValidIsoDate(now)) {
      return { success: false, errors: ["The supplied timestamp is invalid."], task: null };
    }

    const existingSavedAt =
      existingTask && existingTask.meta && isValidIsoDate(existingTask.meta.savedAt)
        ? existingTask.meta.savedAt
        : null;

    return {
      success: true,
      errors: [],
      task: {
        data: cloneData(data),
        meta: {
          taskId,
          season,
          year,
          completed: Boolean(completed),
          savedAt: existingSavedAt || now,
          updatedAt: now,
          version: TASK_SCHEMA_VERSION
        }
      }
    };
  }

  function validateStoredTask(task) {
    const errors = [];
    if (!isPlainObject(task)) return { valid: false, errors: ["Stored task must be an object."] };
    if (!isPlainObject(task.data)) errors.push("Stored task data is missing or invalid.");
    if (!isPlainObject(task.meta)) {
      errors.push("Stored task metadata is missing or invalid.");
      return { valid: false, errors };
    }
    if (typeof task.meta.taskId !== "string" || task.meta.taskId.trim() === "") {
      errors.push("Stored taskId is invalid.");
    }
    if (!VALID_SEASONS.includes(task.meta.season)) errors.push("Stored season is invalid.");
    if (!Number.isInteger(task.meta.year)) errors.push("Stored year is invalid.");
    if (typeof task.meta.completed !== "boolean") {
      errors.push("Stored completed value must be true or false.");
    }
    if (!isValidIsoDate(task.meta.savedAt)) errors.push("Stored savedAt timestamp is invalid.");
    if (!isValidIsoDate(task.meta.updatedAt)) errors.push("Stored updatedAt timestamp is invalid.");
    if (!Number.isInteger(task.meta.version)) errors.push("Stored metadata version is invalid.");

    return { valid: errors.length === 0, errors };
  }

  const api = Object.freeze({
    TASK_SCHEMA_VERSION,
    VALID_SEASONS,
    buildTaskRecord,
    validateTaskInput,
    validateStoredTask
  });

  document.dispatchEvent(
    new CustomEvent("sms:seasonal-task-metadata-ready", {
      detail: { version: TASK_SCHEMA_VERSION }
    })
  );

  return api;
})();